import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Ticket, Timer, CalendarClock, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, guestHeaders, GUEST_KEY } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { CouponReveal } from "@/components/CouponReveal";
import { useLang } from "@/lib/i18n";

const DAY = 86400000;

function useExpiryText() {
  const { t, locale } = useLang();
  return (c) => {
    if (c.aktivert) {
      const min = Math.max(0, Math.ceil((new Date(c.aktivert).getTime() + 30 * 60000 - Date.now()) / 60000));
      return t(`Aktiv nå · ${min} min igjen`, `Active now · ${min} min left`);
    }
    if (!c.utloper) return t("Ingen utløpsdato", "No expiry date");
    const d = new Date(c.utloper);
    const days = Math.max(0, Math.ceil((d.getTime() - Date.now()) / DAY));
    const date = d.toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric", timeZone: "Europe/Oslo" });
    return t(`Gyldig til ${date} · ${days} ${days === 1 ? "dag" : "dager"} igjen`, `Valid until ${date} · ${days} ${days === 1 ? "day" : "days"} left`);
  };
}

const welcomeTitle = (c, t) => (c.type === "velkomst" ? t("En gratis dessert", "A free dessert") : t(c.tittel, c.tittel_en || c.tittel));

function CouponRow({ c, onOpen }) {
  const { t } = useLang();
  const expiryText = useExpiryText();
  const active = !!c.aktivert;
  return (
    <button
      type="button"
      onClick={() => onOpen(c)}
      className={`flex w-full items-center gap-4 rounded-2xl border-2 border-dashed bg-[#282421] p-5 text-left transition-[transform,border-color] duration-200 hover:-translate-y-0.5 ${active ? "border-[#D4AF37]/70" : "border-white/10 hover:border-[#D4AF37]/40"}`}
      data-testid={`guest-coupon-${c.id}`}
    >
      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-[#D4AF37]/10 text-[#D4AF37]">
        {active ? <Timer className="h-6 w-6" /> : <Ticket className="h-6 w-6" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[0.65rem] uppercase tracking-[0.25em] text-[#A8A29E]">
          {c.type === "velkomst" ? t("Velkomstgave", "Welcome gift") : t("Tilbud fra oss", "Offer from us")}
        </p>
        <p className="mt-1 font-serif-display text-xl font-600 leading-tight text-[#FDFBF7]">{welcomeTitle(c, t)}</p>
        <p className={`mt-1.5 flex items-center gap-1.5 text-xs ${active ? "text-[#E5C564]" : "text-[#A8A29E]"}`} data-testid={`guest-coupon-expiry-${c.id}`}>
          <CalendarClock className="h-3.5 w-3.5" /> {expiryText(c)}
        </p>
      </div>
      <ChevronRight className="h-5 w-5 flex-shrink-0 text-[#D4AF37]" />
    </button>
  );
}

function CouponView({ c, fornavn, onBack }) {
  const { t } = useLang();
  const welcome = c.type === "velkomst";
  return (
    <CouponReveal
      tittel={welcomeTitle(c, t)}
      fornavn={fornavn}
      utloper={c.utloper}
      gyldigFra={c.gyldig_fra}
      aktivert={c.aktivert}
      innlost={c.innlost}
      codeBrukt={!!c.aktivert}
      heading={`${t("Hei", "Hi")}, ${fornavn}! ❤️`}
      subheading={welcome ? t("Her er velkomstgaven din.", "Here is your welcome gift.") : t("Her er tilbudet ditt fra oss.", "Here is your offer from us.")}
      onActivate={async () => (await (welcome ? api.post("/coupon/activate", { code: c.code }) : api.post(`/coupon/${c.token}/activate`))).data}
      onRedeem={async () => (await (welcome ? api.post("/coupon/redeem", { code: c.code }) : api.post(`/coupon/${c.token}/redeem`))).data}
      footer={<button type="button" onClick={onBack} className="hover:text-[#D4AF37]" data-testid="guest-back-to-list">← {t("Tilbake til mine kuponger", "Back to my coupons")}</button>}
    />
  );
}

export default function MineKuponger() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem(GUEST_KEY);
    navigate("/logg-inn", { replace: true });
  }, [navigate]);

  const load = useCallback(() => {
    api
      .get("/guest/me", { headers: guestHeaders() })
      .then((res) => setData(res.data))
      .catch((err) => {
        const en = localStorage.getItem("lang") === "en";
        if (err.response?.status === 401) {
          toast.error(en ? "Please log in again" : "Logg inn på nytt");
          logout();
        } else toast.error(en ? "Could not load your coupons" : "Kunne ikke hente kupongene");
      });
  }, [logout]);

  useEffect(() => {
    if (!localStorage.getItem(GUEST_KEY)) {
      navigate("/logg-inn", { replace: true });
      return;
    }
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load, navigate]);

  if (open) return <CouponView c={open} fornavn={data.fornavn} onBack={() => { setOpen(null); load(); }} />;

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#1A1715]" data-testid="mine-kuponger-page">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-[#D4AF37]/10 blur-3xl" />
      <div className="relative z-10 mx-auto flex min-h-screen max-w-xl flex-col px-5 py-8 sm:py-12">
        <header className="flex items-center justify-center animate-fade-in">
          <Logo />
        </header>
        <main className="mt-10 flex-1">
          {!data ? (
            <div className="flex justify-center py-20 text-[#A8A29E]"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <>
              <div className="animate-fade-up">
                <h1 className="font-serif-display text-4xl font-600 text-[#FDFBF7] sm:text-5xl" data-testid="guest-greeting">{t("Hei", "Hi")}, {data.fornavn}!</h1>
                {data.items.length > 0 && (
                  <p className="mt-3 text-base text-[#FDFBF7]/80">
                    {t("Her er kupongene du kan bruke nå. Trykk på en kupong for å aktivere den når du er hos oss.", "Here are the coupons you can use now. Tap a coupon to activate it when you are with us.")}
                  </p>
                )}
              </div>
              {data.items.length === 0 ? (
                <div className="mt-8 rounded-2xl border border-white/10 bg-[#282421] p-8 text-center text-sm text-[#A8A29E]" data-testid="guest-no-coupons">
                  {t("Du har ingen kuponger å bruke akkurat nå. Følg med – vi sender deg tilbud på SMS.", "You have no coupons to use right now. Stay tuned – we'll text you offers.")}
                </div>
              ) : (
                <div className="mt-8 flex flex-col gap-4">
                  {data.items.map((c, i) => (
                    <div key={c.id} className="animate-fade-up" style={{ animationDelay: `${0.08 * (i + 1)}s` }}>
                      <CouponRow c={c} onOpen={setOpen} />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </main>
        <footer className="mt-10 flex justify-center">
          <button type="button" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-xs text-[#A8A29E] hover:border-[#D4AF37] hover:text-[#D4AF37]" data-testid="guest-logout-btn">
            <LogOut className="h-3.5 w-3.5" /> {t("Logg ut", "Log out")}
          </button>
        </footer>
      </div>
    </div>
  );
}
